/**
 * Leaderboard scope strings, the form used in `GET /jobs?scope=…` and in the
 * web app's routing: `global`, `c:<category>`, `s:<category>:<subcategory>`.
 */
import { isValidCategory, isValidScope } from './taxonomy.js';
export function serializeScope(scope) {
    switch (scope.kind) {
        case 'global':
            return 'global';
        case 'category':
            return `c:${scope.category}`;
        case 'subcategory':
            return `s:${scope.category}:${scope.subcategory}`;
    }
}
/** Parse a scope string, returning undefined when it is malformed or unknown. */
export function parseScope(raw) {
    if (raw === undefined || raw === null || raw === '' || raw === 'global') {
        return { kind: 'global' };
    }
    const parts = raw.split(':');
    if (parts[0] === 'c' && parts.length === 2) {
        const category = parts[1];
        if (category && isValidCategory(category))
            return { kind: 'category', category };
        return undefined;
    }
    if (parts[0] === 's' && parts.length === 3) {
        const category = parts[1];
        const subcategory = parts[2];
        if (category && subcategory && isValidScope(category, subcategory)) {
            return { kind: 'subcategory', category, subcategory };
        }
        return undefined;
    }
    return undefined;
}
/* -------------------------------------------------------------------------- */
/* Indexability                                                               */
/* -------------------------------------------------------------------------- */
/**
 * How many published listings a board has to hold before a search engine is
 * invited to index it.
 *
 * The taxonomy is 19 categories over 226 subcategories, which is a great many
 * more addresses than this board has listings. A board below its floor is not
 * hidden — it renders, it links out, and a crawler is told to follow those
 * links — it is simply not offered as a result, because a page holding one
 * listing is a worse copy of that listing's own page and 226 of them is a
 * pattern search engines are explicit about penalising.
 *
 * A board crosses its floor the day another company pays, and nothing has to
 * be redeployed for that to take effect: the count is read when the page is
 * served.
 */
export const SUBCATEGORY_INDEX_THRESHOLD = 3;
/** A category aggregates its subcategories, so it clears at a lower count. */
export const CATEGORY_INDEX_THRESHOLD = 2;
/**
 * Whether a board should be indexed, given how many live listings it holds.
 *
 * The global board and the taxonomy index are always indexable: there is
 * exactly one of each, and neither can be thin in the way a leaf board can.
 */
export function isIndexableScope(scope, listingCount) {
    switch (scope.kind) {
        case 'global':
            return true;
        case 'category':
            return listingCount >= CATEGORY_INDEX_THRESHOLD;
        case 'subcategory':
            return listingCount >= SUBCATEGORY_INDEX_THRESHOLD;
    }
}
/** The site path a scope is served at, e.g. `/c/data-and-ai/data-engineer`. */
export function scopePath(scope) {
    switch (scope.kind) {
        case 'global':
            return '/';
        case 'category':
            return `/c/${scope.category}`;
        case 'subcategory':
            return `/c/${scope.category}/${scope.subcategory}`;
    }
}
/** Parse a `/c/...` site path into a scope, or undefined when it names none. */
export function parseScopePath(path) {
    const segments = path.split('/').filter((segment) => segment !== '');
    if (segments[0] !== 'c')
        return undefined;
    if (segments.length === 2)
        return parseScope(`c:${segments[1] ?? ''}`);
    if (segments.length === 3)
        return parseScope(`s:${segments[1] ?? ''}:${segments[2] ?? ''}`);
    return undefined;
}
//# sourceMappingURL=scope.js.map